import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { interval, of, Subject, Subscription } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { DomSanitizer } from '@angular/platform-browser';
import { FileConvertService } from '@nrwl/rx-files-helpers-service';
import { SentryHandlerService } from '@nrwl/rx-service-sentry';
import { UserFile } from '@nrwl/rx-models';
import { UserFileService } from '@nrwl/rx-service-userfile';
import { WidgetActionData } from '../../../models';
import { WidgetBase } from '../../../models/widget-base';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { SanitizeUrlPipe } from './sanitize-url.pipe';
import { CustomizationImageSourceService } from '../../../services/customization-image-source.service';

@Component({
  selector: 'app-fb-image',
  templateUrl: './fb-image.component.html',
  styleUrls: ['./fb-image.component.scss'],
  imports: [CommonModule, FormsModule, TokenToDivPipe, SanitizeUrlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbImageComponent extends WidgetBase implements OnInit, OnDestroy, DoCheck {
  private readonly customizationImageSource = inject(CustomizationImageSourceService, {
    optional: true,
  });
  private valueCheckInterval: Subscription;
  public BROKEN_IMAGE_PATH = 'assets/images/form-editor/defaults/icon-broken-image.svg';
  private _lastImage;
  public emptyImage = 'assets/images/form-editor/defaults/empty.png';
  public defaultImage = 'assets/images/form-editor/defaults/fb-image.png';
  private _payloadOriginal;
  private lastImage;
  public isImageLoaded = false;
  public UID = 'uid' + Math.random().toString(36).substring(2);

  public _base64;
  private _ready: boolean;
  public get base64ToDisplay() {
    // Sanitizer required by mobile WebView
    return this._base64; //this.domSanitizer.bypassSecurityTrustUrl(this._base64);
  }
  public set base64ToDisplay(value) {
    this._base64 = value;
  }

  public currentImageId;

  @Input()
  public branded;

  public get isBranded() {
    return this.branded;
  }

  private _value;
  @Input()
  public override set payload(value) {
    if (!value) return;

    const newImageId = value.properties.value.value;

    // Only update if the image ID actually changed
    if (this.currentImageId !== newImageId) {
      this.currentImageId = newImageId;
      this._ready = false;
      this._value = value;
      this._payloadOriginal = JSON.parse(JSON.stringify(this._value));

      // Force change detection
      setTimeout(() => {
        this.cd.detectChanges();
        this.loadImage(this._value);
      });
    }
  }
  public override get payload() {
    return this._value;
  }

  // [IOS.WEBVIEW.rxEventEditImageDone] - Receiving modified image
  @HostListener('window:rxEventEditImageDone', ['$event'])
  rxEventEditImageDone(event) {
    if (event.detail && event.detail.id === this.UID && event.detail.data) {
      this.ready.next(false);
      let base64 = decodeURIComponent(event.detail.data);
      base64 = base64.replace('\r\n', '');
      return this.updateImage(base64);
    }
  }

  constructor(
    private userFileService: UserFileService,
    private domSanitizer: DomSanitizer,
    private hostElement: ElementRef<HTMLElement>,
    private convertFile: FileConvertService,
    private _sentryHandlerService: SentryHandlerService,
    private injector: Injector,
    private cd: ChangeDetectorRef,
  ) {
    super(injector);
    this.ready.subscribe((val) => {});
  }

  /**
   * Kiosk and shared-link flows are patient-facing; block markup there.
   */
  public get isReadOnlyMode(): boolean {
    const normalizedMode = (this.mode || '').toLowerCase();

    return normalizedMode === 'kiosk' || this.isRxPortal;
  }

  ngOnInit() {
    this.valueCheckInterval = interval(1000).subscribe(() => {
      const currentValue = this.payload?.properties?.value?.value;
      if (this._lastImage !== currentValue) {
        this._lastImage = currentValue;
        this.currentImageId = currentValue;
        this.ready.next(false);
        this.loadImage(this.payload);
      }
      this.cd.detectChanges();
    });
  }

  override ngOnDestroy() {
    if (this.valueCheckInterval) {
      this.valueCheckInterval.unsubscribe();
    }

    if (super.ngOnDestroy) {
      super.ngOnDestroy();
    }
  }

  ngDoCheck(): void {
    if (this._lastImage !== this.payload?.properties?.value?.value) {
      this._lastImage = this.payload.properties.value.value;
      this.ready.next(false);
      this.cd.detectChanges();
      this.loadImage(this.payload);
    }
  }

  loadImage(value) {
    this.ready.next(true);
    const imageSource =
      (this.isDesign ? this.customizationImageSource?.getDisplaySource(value) : undefined) ??
      value.properties.value.value;
    if (imageSource > 0) {
      this.userFileService
        .get(imageSource)
        .pipe(
          take(1),
          catchError((error) => {
            if (this.isDesign) {
              // RWE-2545: Instead of alert display broken image icon
              //this.action.emit(new WidgetActionData(this, this.payload, 'error'));
            } else {
              this.removeElement();
            }
            return of(null);
          }),
        )
        .subscribe((data: UserFile | any) => {
          if (data?.file) {
            this.loadBase64(data?.file);
          } else {
            this.base64ToDisplay = this.BROKEN_IMAGE_PATH;
            this._ready = true;
            this.isImageLoaded = true;
            this.ready.next(true);
            this.cd.detectChanges();
          }
        });
    } else if (imageSource) {
      this.loadBase64(imageSource);
    } else {
      this.removeElement();
    }
  }

  public removeElement() {
    if (this.isDesign) {
      return;
    }
    this.hostElement.nativeElement.remove();
  }

  public loadBase64(img) {
    this.lastImage = img;
    this.ready.next(false);

    this.convertFile.toBase64viaCanvas(
      img,
      (base64) => {
        this.lastImage = this.base64ToDisplay = base64;
        this.isImageLoaded = true;
        //this.ready.next(true);
        this.cd.detectChanges();
      },
      (error) => {
        this.base64ToDisplay = this.BROKEN_IMAGE_PATH;
        this.isImageLoaded = true;
        this.ready.next(true);
        this.removeElement();
        this.cd.detectChanges();
      },
    );
  }

  public onImageLoadError($event) {
    if (this._base64 !== this.BROKEN_IMAGE_PATH) {
      this.base64ToDisplay = this.BROKEN_IMAGE_PATH;
      this.ready.next(true);
      this.isImageLoaded = true;
      this.removeElement();
    }
    this.cd.detectChanges();
  }

  onImageLoad($event) {
    this._ready = true;
    this.isImageLoaded = true;
    this.ready.next(true);
    this.cd.detectChanges();
  }

  //#region iOS image edit
  public onEditImageClicked($event) {
    if (this.isReadOnlyMode) {
      return;
    }

    try {
      this.action.emit(new WidgetActionData(this, this.payload.properties.value.value));
    } catch (e) {}

    try {
      (window as any).webkit.messageHandlers.rxEventEditImage.postMessage({
        id: this.UID,
        base64: this._base64,
        branded: this.isBranded,
      });
    } catch (e) {}
  }

  public updateImage(base64: string) {
    this.ready.next(false);
    this.cd.detectChanges();

    this.userFileService
      .postBase64(base64)
      .pipe(
        take(1),
        catchError((error) => {
          const eventId = this._sentryHandlerService.handleError(JSON.stringify(error));
          return of(null);
        }),
      )
      .subscribe((result: UserFile | any) => {
        if (result) {
          this.action.emit(
            new WidgetActionData(this, this.payload.properties.value.value, 'migration'),
          );
          //this.base64ToDisplay = base64;
          this.payload.properties.value.value = result.id;
          this.ready.next(true);
        } else {
          this.ready.next(true);
        }
      });
  }
  //#endregion iOS image edit
}
